import { useMemo } from 'react';

import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { datasourceEndpoints, documentsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { getNextCursor } from '@/hooks/queryCache/nextCursor';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  Datasource,
  Document,
  DocumentStatus,
  Documents,
  PartialDocument,
} from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

const DEFAULT_FILTER = '';

export const API = Object.freeze({
  listDocuments: async (
    collectionId: string,
    datasourceId: string,
    cursor?: { cursor: string },
    filter?: string
  ): Promise<Documents> =>
    callGet(endpoints.documents(collectionId, datasourceId, cursor?.cursor, filter)),

  getDocument: async (
    collectionId: string,
    datasourceId: string,
    document_id: string
  ): Promise<Document> => callGet(endpoints.document(collectionId, datasourceId, document_id)),

  createDocument: async (
    collectionId: string,
    datasourceId: string,
    document: Partial<Document>
  ): Promise<Document> => callPost(endpoints.documents(collectionId, datasourceId), document),

  updateDocument: async (
    collectionId: string,
    datasource_id: string,
    { document_id, ...document }: PartialDocument
  ): Promise<Document> =>
    callPut(endpoints.document(collectionId, datasource_id, document_id), document),

  deleteDocument: async (
    collectionId: string,
    datasourceId: string,
    documentId: string
  ): Promise<Document> => callDelete(endpoints.document(collectionId, datasourceId, documentId)),

  getDocumentPreviewUrl: async (
    collectionId: string,
    datasourceId: string,
    documentId: string
  ): Promise<{ preview_url: string; expires_in: number }> =>
    callGet(endpoints.previewDocument(collectionId, datasourceId, documentId)),
});

export const onDocumentUpdated = (
  queryClient: QueryClient,
  collectionId: string,
  datasourceId: string,
  document: Document,
  filter = DEFAULT_FILTER
) => {
  const queryKey = [endpoints.documents(collectionId, datasourceId), filter];
  queryClient.setQueryData(queryKey, (prev: InfiniteData<Documents>) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,

      pages: prev.pages.map((page) => ({
        ...page,
        documents: page.documents.map((acc) =>
          acc.document_id === document.document_id ? document : acc
        ),
      })),
    };
  });
};

export const onDocumentCreated = (
  queryClient: QueryClient,
  collectionId: string,
  datasourceId: string,
  document: Document,
  filter = DEFAULT_FILTER
) => {
  const queryKey = [endpoints.documents(collectionId, datasourceId), filter];
  const queryKeyDatasources = datasourceEndpoints.datasource(collectionId, datasourceId);

  queryClient.setQueryData<Datasource>([queryKeyDatasources], (prev) => {
    if (document.status === DocumentStatus.AVAILABLE) {
      return { ...prev, document_count: prev.document_count + 1 };
    } else {
      return {
        ...prev,
        document_count: prev.document_count + 1,
        [`${document.status}_count`]: prev[`${document.status}_count`] + 1,
      };
    }
  });

  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData(queryKey, (prev: InfiniteData<Documents>) => {
      if (!prev) {
        return prev;
      }

      const newPages = prev.pages.map((page, index) => {
        if (index === 0) {
          return {
            ...page,
            documents: [
              document,
              ...page.documents.filter((acc) => acc.document_id !== document.document_id),
            ],
          };
        }
        return page;
      });

      return { ...prev, pages: newPages };
    });
  }
};

export const onDocumentRemoved = (
  queryClient: QueryClient,
  collectionId: string,
  datasourceId: string,
  document: Document,
  filter = DEFAULT_FILTER
) => {
  const queryKey = [endpoints.documents(collectionId, datasourceId), filter];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData(queryKey, (prev: InfiniteData<Documents>) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          documents: page.documents.filter((acc) => acc.document_id !== document?.document_id),
        })),
      };
    });
  }

  queryClient.removeQueries({
    queryKey: [endpoints.document(collectionId, datasourceId, document.document_id)],
  });

  const queryKeyDatasources = datasourceEndpoints.datasource(collectionId, datasourceId);

  queryClient.setQueryData<Datasource>([queryKeyDatasources], (prev) => {
    if (document.status === DocumentStatus.AVAILABLE) {
      return { ...prev, document_count: prev.document_count - 1 };
    } else {
      return {
        ...prev,
        document_count: prev.document_count - 1,
        [`${document.status}_count`]: prev[`${document.status}_count`] - 1,
      };
    }
  });
};

const useDocuments = (
  collectionId: string,
  datasourceId?: string,
  documentId?: string,
  customFilter?: string
) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const filter = customFilter ?? DEFAULT_FILTER;
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );

  const {
    data: documents,
    status: listStatus,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [endpoints.documents(collectionId, datasourceId), filter],
    queryFn: async ({ pageParam }) =>
      API.listDocuments(collectionId, datasourceId, pageParam, filter),
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
    enabled: !!collectionId && !!datasourceId && canRead,
    refetchInterval: 7 * 1000 /* 7 seconds */,
  });

  const { data: document, status: getStatus } = useQuery<Document, Error>({
    queryKey: [endpoints.document(collectionId, datasourceId, documentId)],
    queryFn: () => API.getDocument(collectionId, datasourceId, documentId),
    enabled: !!collectionId && !!datasourceId && !!documentId,
  });

  const flatDocuments = useMemo(
    () => [...(documents?.pages ?? [])].flatMap((page) => page.documents),
    [documents?.pages]
  );

  const { mutate: updateDocument, status: updateStatus } = useMutation<
    Document,
    Error,
    PartialDocument
  >({
    mutationFn: (data) => API.updateDocument(collectionId, datasourceId, data),
    onSuccess: (resp) => {
      onDocumentUpdated(queryClient, collectionId, datasourceId, resp, filter);
      toast.success(t('collections.documents.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: createDocument, status: createStatus } = useMutation<
    Document,
    Error,
    Partial<Document>
  >({
    mutationFn: (data) =>
      API.createDocument(collectionId, data.datasource_id ?? datasourceId, data),
    onSuccess: (resp) => {
      onDocumentCreated(queryClient, collectionId, resp.datasource_id ?? datasourceId, resp);
      toast.success(t('collections.documents.created', { 0: resp?.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteDocument,
    mutateAsync: deleteDocumentAsync,
    status: deleteStatus,
  } = useMutation<Document, Error, string>({
    mutationFn: (id) => API.deleteDocument(collectionId, datasourceId, id ?? documentId),
    onSuccess: (resp) => {
      const document = flatDocuments.find((d) => resp.document_id === d.document_id);
      onDocumentRemoved(queryClient, collectionId, datasourceId, document, filter);
      toast.success(t('collections.documents.deleted'));
    },
    meta: { errorToast: true },
  });

  const { data: documentPreview, refetch: fetchPreviewUrl } = useQuery<
    { preview_url: string; expires_in: number },
    Error
  >({
    queryKey: [endpoints.previewDocument(collectionId, datasourceId, documentId)],
    queryFn: () => API.getDocumentPreviewUrl(collectionId, datasourceId, documentId),
    enabled: false, // Don't auto-fetch, only when called
  });

  const documentNames = useMemo(() => flatDocuments?.map((d) => d.name), [flatDocuments]);

  return {
    documents: flatDocuments,
    fetchNextPage,
    hasNextPage,
    document,
    getStatus,
    listStatus,
    createStatus,
    deleteStatus,
    updateStatus,
    createDocument,
    deleteDocument,
    deleteDocumentAsync,
    updateDocument,
    documentNames,
    documentPreview,
    fetchPreviewUrl,
  };
};

export default useDocuments;
